import {RefCallback, useCallback, useEffect, useRef} from 'react';

/**
 * Observes an element with a `ResizeObserver` and reports a derived measurement whenever it changes.
 *
 * Returns a callback ref to attach to the element you want to measure. The observer re-attaches
 * automatically when the element mounts/unmounts, and stops observing on unmount.
 *
 * Design notes:
 * - `measure` receives the `ResizeObserverEntry` so it can read reflow-free box metrics (e.g.
 *   `entry.borderBoxSize`) instead of forcing a layout with `getBoundingClientRect`.
 * - Measurements are coalesced to at most one `onMeasure` call per animation frame, and only fire
 *   when the value changes (per `isEqual`). This keeps observers from spamming reflows/renders.
 * - `onMeasure` is a push callback rather than a returned value on purpose: the measurement's
 *   consumer here is a store/selector, so pushing avoids re-rendering the observing component on
 *   every resize. Callers that want the value in render can hold it in their own state inside
 *   `onMeasure`.
 * - In environments without `ResizeObserver` (e.g. NodeJS), a single measurement is taken when the
 *   element attaches.
 *
 * @example
 * const measureHeight = useCallback(
 *   (el: HTMLDivElement, entry: ResizeObserverEntry | null) =>
 *     Math.round(entry?.borderBoxSize?.[0]?.blockSize ?? el.getBoundingClientRect().height),
 *   []
 * );
 * const onHeight = useCallback((height: number) => setState({height}), [setState]);
 * const ref = useMeasuredElement(measureHeight, onHeight);
 * return <div ref={ref} />;
 */
export default function useMeasuredElement<T extends Element, V>(
  measure: (element: T, entry: ResizeObserverEntry | null) => V,
  onMeasure: (value: V) => void,
  isEqual: (a: V, b: V) => boolean = Object.is
): RefCallback<T> {
  // Keep the latest callbacks in refs so the returned ref callback stays stable across renders
  // (and therefore does not detach/reattach the observer on every render).
  const measureRef = useRef(measure);
  measureRef.current = measure;
  const onMeasureRef = useRef(onMeasure);
  onMeasureRef.current = onMeasure;
  const isEqualRef = useRef(isEqual);
  isEqualRef.current = isEqual;

  const observerRef = useRef<ResizeObserver | null>(null);
  const elementRef = useRef<T | null>(null);
  const rafRef = useRef<number | null>(null);
  const pendingEntryRef = useRef<ResizeObserverEntry | null>(null);
  // Wrapped in an object so we can distinguish "no measurement yet" (null) from a measured value.
  const lastValueRef = useRef<{value: V} | null>(null);

  const flush = useCallback(() => {
    rafRef.current = null;
    const element = elementRef.current;
    if (!element) {
      return;
    }
    const value = measureRef.current(element, pendingEntryRef.current);
    pendingEntryRef.current = null;
    if (lastValueRef.current != null && isEqualRef.current(lastValueRef.current.value, value)) {
      return;
    }
    lastValueRef.current = {value};
    onMeasureRef.current(value);
  }, []);

  const scheduleMeasurement = useCallback(
    (entry: ResizeObserverEntry | null) => {
      pendingEntryRef.current = entry;
      if (typeof requestAnimationFrame === 'undefined') {
        flush();
        return;
      }
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(flush);
      }
    },
    [flush]
  );

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      elementRef.current = null;
      if (rafRef.current != null && typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    },
    []
  );

  return useCallback(
    (element: T | null) => {
      if (elementRef.current && observerRef.current) {
        observerRef.current.unobserve(elementRef.current);
      }
      elementRef.current = element;
      // Force the next measurement through even if it matches the previous element's value.
      lastValueRef.current = null;
      if (!element) {
        return;
      }
      if (typeof ResizeObserver === 'undefined') {
        // No ResizeObserver: take a single measurement when the element attaches.
        scheduleMeasurement(null);
        return;
      }
      if (!observerRef.current) {
        observerRef.current = new ResizeObserver((entries) => {
          scheduleMeasurement(entries[entries.length - 1] ?? null);
        });
      }
      observerRef.current.observe(element);
    },
    [scheduleMeasurement]
  );
}
